/**
 * WebGL shader strings and GPU buffer constants for the WebGL renderer.
 *
 * All GLSL source, PLANET_CONST, JS hash mirrors, and buffer-size constants
 * live here so webgl_renderer.js is not cluttered with ~1200 lines of strings.
 */
import { FIREWORKS_PARTICLE_COUNT } from '../constants.js';

const NEBULA_VERT = `#version 300 es
precision highp float;
in  vec2 aPosition;
void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }`;

const NEBULA_FRAG = `#version 300 es
precision mediump float;

uniform float uTime;
uniform vec2  uResolution;
out vec4 fragColor;

const int ORBIT_CAP = 18;

// Kaliset attractor accumulator.
// At each step the point is folded by |q|/dot(q,q) + offset, and a
// Gaussian keyed on the squared-distance delta accumulates into a weighted sum.
float kaliAccum(vec3 q, float seed, int depth) {
    float wt  = 0.0;
    float acc = seed * 0.25;
    float r2  = 0.0;
    for (int k = 0; k < ORBIT_CAP; ++k) {
        if (k >= depth) break;
        float r2Prev = r2;
        r2   = dot(q, q);
        q    = abs(q) / r2 - vec3(0.5, 0.4, 1.487);
        float env  = exp(float(k) * -0.2);
        acc  += env * exp(pow(abs(r2 - r2Prev), 2.2) * -9.025);
        wt   += env;
    }
    return max(0.0, acc * 5.2 / wt - 0.65);
}

// Lord of the Swarm team lattice hash — 2D → 3D.
vec3 latHash3(vec2 g) {
    vec3 ra = fract(cos(g.x * 0.00791 + g.y) * vec3(131917.0, 468293.0, 287541.0));
    vec3 rb = fract(sin(g.x * 0.000317 + g.y) * vec3(812047.0, 103681.0,  97423.0));
    return ra * 0.5 + rb * 0.5;
}

// Sparse point-star field: one candidate per lattice cell, kept only when
// the hash value is extreme enough (pow tail naturally thresholds density).
vec4 starField(vec2 coord) {
    vec2 scaled = coord * 1.9;
    vec2 cell   = floor(scaled * max(uResolution.x, 600.0) * 0.6667);
    vec3 h      = latHash3(cell);
    return vec4(pow(h.y, 50.0));
}

void main() {
    float tm  = uTime;

    vec2 ndc  = gl_FragCoord.xy / uResolution.xy * 2.0 - 1.0;
    vec2 sc   = ndc * uResolution.xy / max(uResolution.x, uResolution.y);

    // Slow drift — constants are exact reciprocals expressed as multipliers
    // so no division is needed in the hot path.
    float sx  = sin(tm * 0.03125);    // period 32
    float sy  = sin(tm * 0.041667);   // period 24
    float sz  = sin(tm * 0.015625);   // period 64

    // Foreground attractor
    vec3 qa   = vec3(sc * 0.4, 0.0) + vec3(0.8, -1.3, 0.0);
    qa += vec3(sx, sy, sz) * 0.45;

    // Colour palette weights (named for clarity, not magic)
    const float W0 = 0.45;   // orange shift
    const float W1 = 0.4;    // green channel
    const float W2 = 0.15;   // blue/contrast
    const float W3 = 0.9;    // overall brightness

    float fa  = kaliAccum(qa, W2, 13);

    // Screen-edge vignette: falls to 0 at the frame boundary
    float vigX = 1.0 - exp((abs(ndc.x) - 1.0) * 6.0);
    float vigY = 1.0 - exp((abs(ndc.y) - 1.0) * 6.0);
    float vig  = vigX * vigY;

    // Background attractor — zooms with paired oscillators
    float zScale = 4.6 + sin(tm * 0.11) * 0.2 + sin(tm * 0.15) * 0.3;
    vec3 qb   = vec3(sc / zScale, 4.0) + vec3(2.0, -1.3, -1.0);
    qb += vec3(sx, sy, sz) * 0.16;

    float fb  = kaliAccum(qb, W3, 18);

    // Orange glow from background layer
    float gs   = mix(0.2625, 0.09, vig);
    vec4  glow = gs * vec4(fb*fb*fb * 2.2, fb*fb * 0.6, fb * 0.08, fb);

    // Static point stars (time-independent coords → no twinkling)
    vec4 stars = starField(sc) + starField(sc * 0.7 + vec2(0.31, 0.57));

    // Foreground layer colour
    float lum  = mix(W3 - 0.3, 1.0, vig);
    vec4  col  = lum * vec4(fa*fa*fa * (0.375 * W2),
                             fa*fa     * (0.225 * W1),
                             fa        * (0.1875 * W3),
                             1.0)
               + glow + stars;

    fragColor = vec4(clamp(col.xyz, 0.0, 1.0), 1.0);
}`;

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Single source of truth for constants shared between the JS texture-sizing
// logic (_planetSphereFill) and the GLSL planet shaders.
// Both sides reference these values, so the JS hash mirrors cannot drift from
// the shader hashes. The shader sources are built by interpolating from this
// table — see _PLANET_FRAG_GAS / PLANET_FRAG_ROCKY below.
// ---------------------------------------------------------------------------
const PLANET_CONST = {
    // Hash multipliers (pcg1 / pcg2 — others derived in shader only)
    H1_MUL: 0.15731,
    H2_X:   0.21341,
    H2_Y:   0.18917,
    H2_Z:   0.23561,
    H_OFF:  47.231,
    // Gas-giant ring presence (~40 % of gas planets)
    RING_SEED_OFF:    0.45,
    RING_THRESHOLD:   0.55,
    // Rocky-planet ring presence (~10 % of rocky planets, independent seed)
    ROCKY_RING_SEED_OFF:   0.95,
    ROCKY_RING_THRESHOLD:  0.90,
    // Ring bounds  rIn = RI_SCALE * h.x + RI_BASE,  rOut = RO_SCALE * h.y + RO_BASE
    RING_BOUNDS_SEED: 0.9,
    RI_SCALE:  0.15,   RI_BASE: 1.05,
    RO_SCALE:  0.195,  RO_BASE: 1.495,
    // Shader type — 50/50 rocky vs gas, independent seed
    SHADER_TYPE_SEED: 0.50,  // pcg1(seed + 0.50) < 0.5 → rocky, >= 0.5 → gas
    // Texture sizing
    DEFAULT_FILL: 0.745, // sphere fill when there are no rings
    FILL_MARGIN:  1.20,  // sprite is FILL_MARGIN × tight ring fit
};

const _PC = PLANET_CONST;

// JS mirrors of the GLSL pcg1 / pcg2 hash functions, used to compute per-planet
// ring geometry so the texture can be sized to exactly contain the ring.
/** @param {number} x @returns {number} */
function _glFract(x) { return x - Math.floor(x); }
/** @param {number} p @returns {number} */
function _glPcg1(p) {
    p = _glFract(p * _PC.H1_MUL);
    p = p * p + _PC.H_OFF;
    p = p * (p + p);
    return _glFract(p);
}
/** @param {number} p @returns {[number, number]} */
function _glPcg2(p) {
    let x = _glFract(p * _PC.H2_X), y = _glFract(p * _PC.H2_Y), z = _glFract(p * _PC.H2_Z);
    const d = x * (z + _PC.H_OFF) + y * (x + _PC.H_OFF) + z * (y + _PC.H_OFF);
    x += d; y += d; z += d;
    return [_glFract((y + x) * z), _glFract((y + z) * x)];
}

/**
 * Return the sphere fill fraction (sphere edge / texture half-width) for a
 * given planet seed.  For ring planets the value is chosen so the ring outer
 * edge lands inside the texture, with FILL_MARGIN headroom.
 * @param {number} seed
 * @returns {number}
 */
function _planetSphereFill(seed) {
    if (_glPcg1(seed + _PC.RING_SEED_OFF) < _PC.RING_THRESHOLD - 0.08) return _PC.DEFAULT_FILL;
    const h2 = _glPcg2(seed + _PC.RING_BOUNDS_SEED);
    const outerBound = _PC.RO_SCALE * h2[1] + _PC.RO_BASE;
    return 1.0 / (outerBound * _PC.FILL_MARGIN);
}

/**
 * Returns true if this planet should use the rocky shader.
 * Probability of being rocky is high for the smallest planet in the level
 * and low for the largest, with a smooth transition in between.
 * 10 % floor so even the largest planet can still be rocky (and vice-versa).
 * @param {number} seed
 * @param {number} relSize  Normalised size within the level: 0 = smallest, 1 = largest.
 */
function _glPlanetIsRocky(seed, relSize) {
    const smooth  = relSize * relSize * (3 - 2 * relSize);  // cubic smoothstep
    const gasProb = 0.10 + 0.80 * smooth;                   // 10 % → 90 %
    return _glPcg1(seed + _PC.SHADER_TYPE_SEED) >= gasProb;
}

/** Same as _planetSphereFill but uses the rocky-planet ring seed (10 % chance).
 *  Uses a conservative threshold 0.08 below the GLSL value to absorb f32/f64
 *  precision divergence — seeds passed as float uniforms can hash slightly
 *  differently in 32-bit GLSL vs 64-bit JS, so we'd rather over-size the
 *  texture for a few no-ring planets than clip the ring on ring planets. */
function _rockyPlanetSphereFill(seed) {
    if (_glPcg1(seed + _PC.ROCKY_RING_SEED_OFF) < _PC.ROCKY_RING_THRESHOLD - 0.08) return _PC.DEFAULT_FILL;
    const h2 = _glPcg2(seed + _PC.RING_BOUNDS_SEED);
    const outerBound = _PC.RO_SCALE * h2[1] + _PC.RO_BASE;
    return 1.0 / (outerBound * _PC.FILL_MARGIN);
}

const PLANET_VERT = `#version 300 es
precision highp float;
in  vec2 aPosition;
void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }`;

// Lord of the Swarm — procedural gas-giant shader (currently held in reserve;
// the active shader is PLANET_FRAG_ROCKY below). The leading underscore on
// _PLANET_FRAG_GAS marks it as intentionally unused for the lint pass.
// Techniques used are all standard public-domain computer-graphics mathematics:
// ray–sphere intersection, cubic-Hermite value noise on a 3-D lattice, fractal
// Brownian motion, HSV→RGB conversion, and ray–plane intersection for rings.
// Hash and ring constants are interpolated from PLANET_CONST (above) so JS and
// GLSL share a single source of truth.
const _PLANET_FRAG_GAS = `#version 300 es
precision highp float;

uniform float uSeed;
uniform float uFill;   // sphere fill fraction: sphere-edge / texture half-width
uniform vec2  uResolution;
out vec4 fragColor;

const float PI        = 3.141593;
const float VIEW_ELEV = 0.4887;  // camera elevation ~28° above equator

// Hash constants (from JS PLANET_CONST — single source of truth)
const float H1_MUL = ${_PC.H1_MUL};
const float H_OFF  = ${_PC.H_OFF};
const vec3  H_VEC3 = vec3(${_PC.H2_X}, ${_PC.H2_Y}, ${_PC.H2_Z});
const vec4  H_VEC4 = vec4(${_PC.H2_X}, ${_PC.H2_Y}, ${_PC.H2_Z}, 0.19793);

// ---------------------------------------------------------------------------
// Hash family — Lord of the Swarm team constants.
// Technique: fract-scale → self-mix → fract.
// ---------------------------------------------------------------------------
float pcg1(float v) {
    v = fract(v * H1_MUL);
    v = v * v + H_OFF;
    v = v * (v + v);
    return fract(v);
}

vec2 pcg2(float v) {
    vec3 t = fract(vec3(v) * H_VEC3);
    t += dot(t, t.zxy + H_OFF);
    return fract((t.yy + t.xz) * t.zx);
}

vec3 pcg3(float v) {
    vec3 t = fract(vec3(v) * H_VEC3);
    t += dot(t, t.zxy + H_OFF);
    return fract((t.xxz + t.yzy) * t.zyx);
}

vec3 pcg3(vec3 v) {
    v = fract(v * H_VEC3);
    v += dot(v, v.yzx + H_OFF);
    return fract((v.xxz + v.yzz) * v.zyx);
}

vec4 pcg4(float v) {
    vec4 t = fract(vec4(v) * H_VEC4);
    t += dot(t, t.wzxy + H_OFF);
    return fract((t.xxyz + t.yzzw) * t.zywx);
}

// ---------------------------------------------------------------------------
// Smooth value noise — cubic Hermite interpolation on a 3-D lattice
// ---------------------------------------------------------------------------
float vn1(float p) {
    float i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
    return 1.0 - 2.0 * mix(pcg1(i), pcg1(i + 1.0), u);
}

vec3 vn3(vec3 p) {
    vec3 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
    return 1.0 - 2.0 * mix(
        mix(mix(pcg3(i),             pcg3(i+vec3(1,0,0)), u.x),
            mix(pcg3(i+vec3(0,1,0)), pcg3(i+vec3(1,1,0)), u.x), u.y),
        mix(mix(pcg3(i+vec3(0,0,1)), pcg3(i+vec3(1,0,1)), u.x),
            mix(pcg3(i+vec3(0,1,1)), pcg3(i+vec3(1,1,1)), u.x), u.y), u.z);
}

// ---------------------------------------------------------------------------
// Fractal Brownian Motion — 3 octaves is sufficient for a baked static texture.
// Matrix M is non-orthogonal so successive octaves sample along different axes,
// breaking the axis-aligned banding that plain fBm produces.
// Values chosen to give roughly balanced shear in all three directions.
// ---------------------------------------------------------------------------
float fbm1(float p) {
    float a = vn1(p); p *= 2.0;
    a += 0.5  * vn1(p); p *= 2.0;
    a += 0.25 * vn1(p);
    return a / 1.75;
}

const mat3 M = mat3( 0.7346, -1.4823,  1.0571,
                    -1.6284, -0.8347, -0.5129,
                     0.8912, -0.9465, -1.4378);

vec3 fbm3(vec3 p) {
    vec3 a = vn3(p); p = M * p;
    a += 0.5  * vn3(p); p = M * p;
    a += 0.25 * vn3(p);
    return a / 1.75;
}

// ---------------------------------------------------------------------------
// Standard HSV → RGB (piecewise-linear hue ramp)
// ---------------------------------------------------------------------------
vec3 hsvRgb(float h, float s, float v) {
    vec3 c = abs(fract(h + vec3(1.0, 0.6667, 0.3333)) * 6.0 - 3.0);
    return v * mix(vec3(1.0), clamp(c - 1.0, 0.0, 1.0), s);
}

// ---------------------------------------------------------------------------
// Ring system — ray / equatorial-plane intersection, opacity from fBm.
// Bounds in planet-radii; inner > 1.0 keeps the ring outside the sphere.
// ---------------------------------------------------------------------------
float ringDisc(vec3 org, vec3 dir, vec3 spinAxis, float ps,
               out float tPlane, out float planeR, out vec3 planePt) {
    vec2  rh   = pcg2(ps + ${_PC.RING_BOUNDS_SEED});
    float rIn  = ${_PC.RI_SCALE} * rh.x + ${_PC.RI_BASE};
    float rOut = ${_PC.RO_SCALE} * rh.y + ${_PC.RO_BASE};
    tPlane  = dot(-org, spinAxis) / dot(dir, spinAxis);
    planePt = org + tPlane * dir;
    planeR  = length(planePt);
    float mask = smoothstep(rIn, rIn + 0.1, planeR) * smoothstep(rOut, rOut - 0.1, planeR);
    mask *= smoothstep(-0.5, 1.0, fbm1(6.0 * (planeR + 3.0 * ps)));
    return clamp(mask, 0.0, 1.0);
}

void main() {
    float ps = uSeed;

    // Camera: eye at distance 20 for near-orthographic projection.
    // EYE_TAN = tan(asin(1/20)); zoom keeps the sphere at exactly uFill of the texture.
    const float EYE_DIST = 20.0;
    const float EYE_TAN  = 0.0501;
    vec3  camPos = vec3(0.0, -cos(VIEW_ELEV), sin(VIEW_ELEV)) * EYE_DIST;
    float fov    = uFill / EYE_TAN;
    vec3  fwd    = normalize(-camPos);
    vec3  rgt    = normalize(cross(fwd, vec3(0.0, 0.0, 1.0)));
    vec3  upv    = cross(rgt, fwd);

    vec2 sc = 2.0 * gl_FragCoord.xy - uResolution;
    vec3 rd = normalize(sc.x * rgt + sc.y * upv + fov * fwd * uResolution.y);

    // Spin axis: within 20° of camera-up so all planets appear roughly upright.
    float tiltMag  = pcg1(ps + 0.10) * 0.36;
    float tiltDir  = pcg1(ps + 0.11) * 2.0 * PI;
    vec3  spinAxis = normalize(upv + tiltMag * (cos(tiltDir) * rgt + sin(tiltDir) * fwd));

    float freqScale = 1.0 + 6.0 * pcg1(ps + 0.2);

    vec4 ph    = pcg4(ps + 0.3);
    // Saturation reduced to 1/3 of original so faction colour overlays read clearly.
    vec3 baseCol = hsvRgb(ph.x, 0.17, 0.5 + 0.2 * ph.y);
    vec3 varA    = hsvRgb(ph.x,                0.10, 0.5 + 0.2 * ph.y + 0.3 * ph.w) - baseCol;
    vec3 varB    = hsvRgb(ph.x + 0.4 * ph.z,  0.17, 0.5 + 0.2 * ph.y)              - baseCol;
    vec3 varC    = hsvRgb(ph.x + 0.4 * ph.z,  0.10, 0.5 + 0.2 * ph.y - 0.4 * ph.w) - baseCol;

    vec3 rh3      = pcg3(ps + 0.4);
    vec3 ringCol  = hsvRgb(rh3.x, 0.07, 0.5 + 0.2 * rh3.y);
    vec3 ringVar  = hsvRgb(rh3.x + 0.3 * rh3.z, 0.07, 0.7 + 0.2 * rh3.y) - ringCol;
    float ringMask = smoothstep(${_PC.RING_THRESHOLD}, ${_PC.RING_THRESHOLD + 0.20}, pcg1(ps + ${_PC.RING_SEED_OFF}));

    // Ray–unit-sphere intersection (standard quadratic).
    // Smooth hit over one screen-pixel using fwidth for antialiased edge.
    float tc  = dot(camPos, rd);
    float det = tc * tc - (dot(camPos, camPos) - 1.0);
    float hitEdge = fwidth(det);
    float hit = smoothstep(-hitEdge, hitEdge, det);
    det = max(det, 0.0);
    float hitT   = -tc - sqrt(det);
    vec3  surfPt = camPos + hitT * rd;

    // Build orthonormal planet frame; mat3 is orthonormal so transpose = inverse
    vec3 axX      = normalize(cross(spinAxis, vec3(0.0, 0.0, 1.0)));
    vec3 axY      = cross(spinAxis, axX);
    mat3 toLocal  = transpose(mat3(spinAxis, axX, axY));

    vec3 sp    = toLocal * surfPt;
    vec3 bands = fbm3(freqScale * vec3(1.0, 0.05, 0.05) * sp + ps);
    vec3 col   = baseCol + varA * bands.x + varB * bands.y + varC * bands.z;

    // Limb darkening — view-angle falloff for 3-D appearance (no directional light)
    col *= hit * max(0.0, dot(surfPt, -rd));

    // Ring
    float tPlane, planeR;
    vec3  planePt;
    float rAlpha = ringDisc(camPos, rd, spinAxis, ps, tPlane, planeR, planePt) * ringMask;
    float rBlend = step(0.0, tPlane) * (1.0 - hit * step(hitT, tPlane)) * rAlpha;
    ringCol += ringVar * fbm1(15.0 * (planeR + 3.0 * ps));
    col = mix(col, ringCol, rBlend);

    col *= 1.1;
    fragColor = vec4(col, max(hit, rBlend));
}`;

// ---------------------------------------------------------------------------
// Lord of the Swarm — procedural rocky-planet shader.
//
// Structure mirrors the reference Shadertoy crater shader:
//   map(p)    — converts sphere point to lat/lon UV, returns crater height
//   normal(p) — 6-tap finite differences on map() (identical technique)
//   main()    — ray-sphere, map/normal, diffuse light * height-based colour mix
//
// Instead of a texture for iChannel0 we generate height procedurally:
// four independent 2-D Voronoi crater octaves at vastly different scales.
// An archetype hash [0..1] gates each octave so planets range from
// "Europa" (sparse giant basins) through "Moon/Mercury" to "Callisto"
// (densely packed small pits).
// ---------------------------------------------------------------------------
const PLANET_FRAG_ROCKY = `#version 300 es
precision highp float;

uniform float uSeed;
uniform float uFill;
uniform vec2  uResolution;
out vec4 fragColor;

const float PI        = 3.141593;
const float VIEW_ELEV = 0.4887;
const float SPH_TAN   = 0.2981;

// Hash constants — single source of truth via PLANET_CONST
const float H1_MUL = ${_PC.H1_MUL};
const float H_OFF  = ${_PC.H_OFF};
const vec3  H_VEC3 = vec3(${_PC.H2_X}, ${_PC.H2_Y}, ${_PC.H2_Z});

float pcg1(float v) {
    v = fract(v * H1_MUL); v = v*v + H_OFF; v = v*(v+v); return fract(v);
}
vec2 pcg2(float v) {
    vec3 t = fract(vec3(v) * H_VEC3);
    t += dot(t, t.zxy + H_OFF);
    return fract((t.yy + t.xz) * t.zx);
}
vec3 pcg3(vec3 v) {
    v = fract(v * H_VEC3); v += dot(v, v.yzx + H_OFF);
    return fract((v.xxz + v.yzz) * v.zyx);
}

// 1-D value noise + fBm — needed for ring opacity texture
float vn1(float p) {
    float i = floor(p), f = fract(p), u = f*f*(3.0-2.0*f);
    return 1.0 - 2.0 * mix(pcg1(i), pcg1(i+1.0), u);
}
float fbm1(float p) {
    float a = vn1(p); p *= 2.0;
    a += 0.5*vn1(p); p *= 2.0;
    a += 0.25*vn1(p);
    return a / 1.75;
}

// Ring disc — identical to the gas-giant ring system.
// Bounds constants interpolated from PLANET_CONST for single source of truth.
float ringDisc(vec3 org, vec3 dir, vec3 spinAxis, float ps,
               out float tPlane, out float planeR, out vec3 planePt) {
    vec2  rh   = pcg2(ps + ${_PC.RING_BOUNDS_SEED});
    float rIn  = ${_PC.RI_SCALE}  * rh.x + ${_PC.RI_BASE};
    float rOut = ${_PC.RO_SCALE} * rh.y + ${_PC.RO_BASE};
    tPlane  = dot(-org, spinAxis) / dot(dir, spinAxis);
    planePt = org + tPlane * dir;
    planeR  = length(planePt);
    float mask = smoothstep(rIn, rIn+0.1, planeR) * smoothstep(rOut, rOut-0.1, planeR);
    mask *= smoothstep(-0.5, 1.0, fbm1(6.0 * (planeR + 3.0*ps)));
    return clamp(mask, 0.0, 1.0);
}

// Spherical UV — local-y is the pole axis (caller rotates p first).
vec2 sphereUV(vec3 p) {
    return vec2(
        (atan(p.x, p.z) + PI) / (2.0 * PI),
        acos(clamp(p.y / length(p), -1.0, 1.0)) / PI
    );
}

// Full crater contribution at offset delta from crater centre, in cell units.
// Models the three elements from the reference C code:
//   1. crater_dimple  — bowl interior (floor is raised/flat, walls slope down)
//   2. add_cone ring  — raised ejecta rim just outside the crater edge
//   3. add_rays       — radial ejecta streaks extending outward
// delta   : fragment position relative to crater centre (cell-unit coords)
// rad     : crater radius in cell units
// seed    : per-crater hash isolation
// doRays  : enable ray streaks (expensive — only for large craters)
float craterH(vec2 delta, float rad, float seed, bool doRays) {
    float dist = length(delta);
    float r    = dist / rad;   // 0 at centre, 1 at rim
    float h    = 0.0;

    // 1. Bowl: deepest at center, rises parabolically to 0 at the rim.
    //    Matches crater_dimple intent: centre is lowest, walls slope upward.
    if (r < 1.0) {
        float bowl = (1.0 - r) * (1.0 - r);  // 1 at centre, 0 at rim
        h -= 0.85 * bowl;
    }

    // 2. Ejecta rim: raised annulus just outside the crater edge
    float rimR = abs(r - 1.02) / 0.18;
    h += 0.52 * exp(-rimR * rimR * 5.0);

    // 3. Ejecta rays: radial streaks beyond the rim
    //    mirrors add_rays — N spokes, each a cone trail that fades with distance
    if (doRays && r > 0.9 && r < 5.5) {
        float angle  = atan(delta.y, delta.x);
        int   nRays  = 7 + int(pcg1(seed + 0.07) * 9.0);  // 7–16 per crater
        float maxRay = 0.0;
        for (int i = 0; i < 16; i++) {
            if (i >= nRays) break;
            float rayA   = pcg1(seed + float(i) * 0.137 + 31.7) * 2.0 * PI;
            float dA     = abs(mod(angle - rayA + 3.0 * PI, 2.0 * PI) - PI);
            float beyond = max(0.0, r - 1.0);
            float spread = 0.18 * exp(-beyond * 0.6);  // fans out near rim, narrows far
            float stripe = max(0.0, 1.0 - dA / spread);
            float fade   = max(0.0, 1.0 - beyond / 4.5);
            maxRay       = max(maxRay, stripe * fade);
        }
        h += 0.38 * maxRay;
    }
    return h;
}

// 2-D Voronoi crater layer (UV space) — for large/medium craters.
// rLo/rHi in cell-units; scale = cells per UV unit.
float craterLayerUV(vec2 uv, float scale, float density,
                    float rLo, float rHi, float seed, bool doRays) {
    vec2 p  = uv * scale;
    vec2 ip = floor(p);
    vec2 fp = fract(p);
    float h = 0.0;
    for (int dx = -1; dx <= 1; dx++) for (int dy = -1; dy <= 1; dy++) {
        vec3  rnd  = pcg3(vec3(ip + vec2(float(dx), float(dy)), seed));
        if (rnd.z > density) continue;
        vec2  ctr  = vec2(float(dx), float(dy)) + 0.15 + 0.70 * rnd.xy;
        float rad  = mix(rLo, rHi, pcg1(rnd.x * 7.3 + seed));
        vec2  dlt  = fp - ctr;
        if (length(dlt) > rad * (doRays ? 5.6 : 1.2)) continue;
        h += craterH(dlt, rad, rnd.x * 31.7 + seed, doRays);
    }
    return h;
}

// 3-D Voronoi crater layer (world space) — seamless, no polar distortion.
// rLo/rHi in cell-units; scale = cells per world unit.
float craterLayerVol(vec3 p, float density, float rLo, float rHi, float seed) {
    vec3 ip = floor(p);
    vec3 fp = fract(p);
    float h = 0.0;
    for (int dz=-1;dz<=1;dz++) for (int dy=-1;dy<=1;dy++) for (int dx=-1;dx<=1;dx++) {
        vec3  rnd  = pcg3(ip + vec3(float(dx),float(dy),float(dz)) + seed);
        if (rnd.z > density) continue;
        vec3  ctr  = vec3(float(dx),float(dy),float(dz)) + 0.15 + 0.70 * rnd;
        float rad  = mix(rLo, rHi, pcg1(rnd.x * 7.3 + seed));
        vec3  dlt  = fp - ctr;
        if (length(dlt) > rad * 1.2) continue;
        // Treat the 3-D delta as a 2-D UV delta using two orthogonal components
        h += craterH(dlt.xy, rad, rnd.x * 31.7 + seed, false);
    }
    return h;
}

// Scale reference for a 128×128 texture (sphere fills 74.5 %  →  ≈ 95px diam):
//   UV scale  sc → crater diam  ≈  2 * rHi / sc * 149 px
//   3D scale  sc → crater diam  ≈  2 * rHi / sc * 47.5 px
//
//   A  UV 2–5:   basins   20–65 px
//   B  UV 6–12:  medium    5–16 px
//   C  3D 2–5:   small     4–10 px  (seamless)
//   D  3D 5–12:  tiny      2–4 px   (fractal stack, creates fine texture)
float mapH(vec3 p, float ps) {
    // Spin axis: always horizontal (random azimuth in the xz-plane).
    // Poles point sideways so the UV pinch is never front-facing.
    float sa  = pcg1(ps + 0.15) * PI;
    // lp.z is the longitude-zero reference direction.
    // Choosing it to equal -dot(p, cameraFwd) puts the UV seam (where lp.z < 0)
    // on the back hemisphere, always hidden from the camera.
    // cameraFwd = (0, cos(VIEW_ELEV), -sin(VIEW_ELEV)), so -dot = sin*p.z - cos*p.y.
    vec3  lp  = vec3(p.x * sin(sa) - p.z * cos(sa),
                     p.x * cos(sa) + p.z * sin(sa),
                     sin(VIEW_ELEV) * p.z - cos(VIEW_ELEV) * p.y);
    vec2 uv = sphereUV(lp);
    float h  = 0.0;

    // A — giant basins with ejecta rays
    float scA = mix(2.0,  5.0,  pcg1(ps + 1.1));
    float deA = mix(0.10, 0.75, pcg1(ps + 1.2));
    float amA = mix(0.30, 1.20, pcg1(ps + 1.3));
    h += amA * craterLayerUV(uv, scA, deA, 0.24, 0.42, ps + 1.0, true);

    // B — medium craters, rim but no rays
    float scB = mix(6.0, 12.0,  pcg1(ps + 2.1));
    float deB = mix(0.25, 0.85, pcg1(ps + 2.2));
    float amB = mix(0.25, 0.90, pcg1(ps + 2.3));
    h += amB * craterLayerUV(uv, scB, deB, 0.15, 0.30, ps + 2.0, false);

    // C — small craters, 3-D world space (seamless)
    float scC = mix(2.0, 5.0,   pcg1(ps + 3.1));
    float deC = mix(0.35, 0.88, pcg1(ps + 3.2));
    float amC = mix(0.20, 0.70, pcg1(ps + 3.3));
    h += amC * craterLayerVol(p * scC, deC, 0.18, 0.36, ps + 3.0);

    // D — fine cratering: fractal stack of 3-D octaves
    float scD = mix(5.0, 12.0,  pcg1(ps + 4.1));
    float deD = mix(0.50, 0.92, pcg1(ps + 4.2));
    float amD = mix(0.12, 0.45, pcg1(ps + 4.3));
    h += amD        * craterLayerVol(p * scD,       deD, 0.12, 0.24, ps + 4.0);
    h += amD * 0.50 * craterLayerVol(p * scD * 2.3, deD, 0.12, 0.24, ps + 4.5);
    h += amD * 0.25 * craterLayerVol(p * scD * 5.1, deD, 0.12, 0.24, ps + 5.0);

    return h;
}

// 6-tap finite-difference normal — identical technique to the reference shader.
vec3 surfNorm(vec3 p, float ps, float bumpStr) {
    float e = 8.0e-4;
    vec3 g = vec3(
        mapH(p + vec3(e,0,0), ps) - mapH(p - vec3(e,0,0), ps),
        mapH(p + vec3(0,e,0), ps) - mapH(p - vec3(0,e,0), ps),
        mapH(p + vec3(0,0,e), ps) - mapH(p - vec3(0,0,e), ps)
    );
    p += bumpStr * g / (2.0 * e);
    return normalize(p);
}

// Seven-stop terrain palette — evenly spaced across [0,1]:
//   0  dark crimson  →  1  rust  →  2  gold  →  3  dark olive
//   →  4  forest green  →  5  blue-grey  →  6  pale blue
vec3 terrainPalette(float t) {
    t = clamp(t, 0.0, 1.0) * 6.0;
    vec3 c0 = vec3(0.42, 0.09, 0.07);  // dark crimson
    vec3 c1 = vec3(0.60, 0.28, 0.14);  // rust / reddish-brown
    vec3 c2 = vec3(0.66, 0.52, 0.16);  // gold / tan
    vec3 c3 = vec3(0.46, 0.44, 0.18);  // dark olive
    vec3 c4 = vec3(0.18, 0.34, 0.16);  // dark forest green
    vec3 c5 = vec3(0.48, 0.52, 0.56);  // blue-grey
    vec3 c6 = vec3(0.52, 0.64, 0.78);  // pale blue
    float f = fract(t);
    int   i = int(t);
    if (i == 0) return mix(c0, c1, f);
    if (i == 1) return mix(c1, c2, f);
    if (i == 2) return mix(c2, c3, f);
    if (i == 3) return mix(c3, c4, f);
    if (i == 4) return mix(c4, c5, f);
    return              mix(c5, c6, f);
}

void main() {
    float ps = uSeed;

    // Camera — same setup as gas variant
    // Eye at distance 20: sphere subtends tan(asin(1/20)) ≈ 0.050 radians.
    // zoom adjusted to keep the sphere filling the same fraction of the texture.
    // This gives nearly orthographic projection (very low perspective distortion).
    const float EYE_DIST = 20.0;
    const float EYE_TAN  = 0.0501;  // tan(asin(1/EYE_DIST))
    vec3  camPos = vec3(0.0, -cos(VIEW_ELEV), sin(VIEW_ELEV)) * EYE_DIST;
    float fov    = uFill / EYE_TAN;
    vec3  fwd    = normalize(-camPos);
    vec3  rgt    = normalize(cross(fwd, vec3(0.0, 0.0, 1.0)));
    vec3  upv    = cross(rgt, fwd);

    vec2 sc = 2.0 * gl_FragCoord.xy - uResolution;
    vec3 rd = normalize(sc.x * rgt + sc.y * upv + fov * fwd * uResolution.y);

    // Ray–sphere intersection.  Smooth hit over one screen-pixel for AA edge.
    float tc  = dot(camPos, rd);
    float det = tc*tc - (dot(camPos,camPos) - 1.0);
    float hitEdge = fwidth(det);
    float hit = smoothstep(-hitEdge, hitEdge, det);
    det = max(det, 0.0);
    float hitT = -tc - sqrt(det);
    vec3  q    = camPos + hitT * rd;

    // Visual spin axis — within 20° of the camera's up direction so all planets
    // appear roughly "upright" (north pole near screen-top).  A random tilt
    // magnitude and direction add subtle variety without crazy orientations.
    float vTiltMag = pcg1(ps + 0.60) * 0.36;           // 0 – ~20° of tilt
    float vTiltDir = pcg1(ps + 0.61) * 2.0 * PI;       // random tilt direction
    vec3  vAxis    = normalize(upv + vTiltMag * (cos(vTiltDir) * rgt + sin(vTiltDir) * fwd));

    // Rings — 10 % chance.  Computed before sphere surface so that ring pixels
    // on the background (ray misses sphere) are handled correctly.
    float ringMask = smoothstep(${_PC.ROCKY_RING_THRESHOLD - 0.04},
                                ${_PC.ROCKY_RING_THRESHOLD + 0.04},
                                pcg1(ps + ${_PC.ROCKY_RING_SEED_OFF}));
    float tPlane = 0.0; float planeR = 0.0; vec3 planePt = vec3(0.0);
    float rAlpha = 0.0; float rBlend = 0.0;
    vec3  ringCol = vec3(0.0);
    if (ringMask > 0.0) {
        vec2 rh3  = pcg2(ps + 0.4);
        ringCol   = vec3(0.50 + 0.20*rh3.x, 0.44 + 0.16*rh3.y, 0.32 + 0.12*rh3.x);
        rAlpha    = ringDisc(camPos, rd, vAxis, ps, tPlane, planeR, planePt) * ringMask;
        rBlend    = step(0.0, tPlane) * (1.0 - hit * step(hitT, tPlane)) * rAlpha;
        ringCol  += vec3(0.15, 0.12, 0.08) * fbm1(15.0 * (planeR + 3.0*ps));
    }

    // If the ray missed the sphere entirely and there's no ring, discard.
    // Early-out for pixels clearly outside the sphere and ring — keep the
    // silhouette transition zone (det > -hitEdge) alive for the smooth AA edge.
    if (det < -hitEdge && rBlend < 0.01) { fragColor = vec4(0.0); return; }

    // ---- Sphere surface ----
    float c    = hit > 0.5 ? mapH(q, ps) : 0.0;
    vec3  norm = hit > 0.5 ? surfNorm(q, ps, 0.035) : vAxis;

    vec3  L     = normalize(vec3(-3.0, 1.5, 2.5));
    float diff  = clamp(dot(norm, L), 0.0, 1.0);
    float light = (0.18 + 0.82 * diff) * mix(0.7, 1.0, max(0.0, dot(q, -rd)));

    vec3 highCol = terrainPalette(pcg1(ps + 0.31)) * (0.80 + 0.35 * pcg1(ps + 0.32));
    vec3 deepCol = terrainPalette(pcg1(ps + 0.33)) * (0.45 + 0.20 * pcg1(ps + 0.34));
    vec3 col     = light * mix(highCol, deepCol, smoothstep(0.50, -1.10, c));

    float vLat = dot(q, vAxis);
    vec3  iceCol = vec3(0.88, 0.93, 0.98) * light;
    if (pcg1(ps + 0.70) < 0.25) {
        float edge = 1.0 - mix(0.15, 0.60, pcg1(ps + 0.71));
        col = mix(col, iceCol, smoothstep(edge - 0.05, edge + 0.05, vLat));
    }
    if (pcg1(ps + 0.72) < 0.25) {
        float edge = 1.0 - mix(0.15, 0.60, pcg1(ps + 0.73));
        col = mix(col, iceCol, smoothstep(edge - 0.05, edge + 0.05, -vLat));
    }

    float waterHash  = pcg1(ps + 0.80);
    float waterLevel = mix(-0.20, 0.30, pcg1(ps + 0.81));
    if (waterHash < 0.25 && c < waterLevel) {
        float depth    = clamp((waterLevel - c) / 0.40, 0.0, 1.0);
        vec3  waterCol = mix(vec3(0.35, 0.55, 0.72), vec3(0.08, 0.18, 0.42), depth) * light;
        waterCol      += 0.25 * pow(max(0.0, dot(norm, normalize(L - rd))), 32.0);
        col = waterCol;
    }

    col *= hit;  // zero out if sphere was missed (ring-only pixels)

    // Blend ring over sphere (or background)
    col = mix(col, ringCol * mix(0.35, 1.0, light), rBlend);

    col = pow(max(col, vec3(0.0)), vec3(1.0 / 2.2));
    fragColor = vec4(col, max(hit, rBlend));
}`;

// ---------------------------------------------------------------------------
// Instanced explosion shader — Lord of the Swarm
//
// Cheap-and-cheerful: a soft circular sprite with a small angular sine wobble
// for the rim, a single radial ramp from a warm amber core to a faction-tinted
// outer rim, and a time-based fade. No volumetric noise, no smoke band. This
// keeps additive stacking from saturating to white (the core never starts
// near-white) and avoids the 4-iter gyroid noise that dominated frag cost.
//
// Instance data layout (32 bytes / 8 floats):
//   [cx, cy, radius, progress, seed, r, g, b]   ← rim (faction) colour only
// ---------------------------------------------------------------------------
const EXPLOSION_VERT = `#version 300 es
precision highp float;

in vec2  aQuad;
in vec2  aCenter;
in float aRadius;
in float aProgress;
in float aSeed;
in vec3  aOuterColor;

uniform mat3 uM;

out vec2  vUV;
out float vProgress;
out float vSeed;
out vec3  vOuterColor;

void main() {
    vec2 world  = aCenter + aRadius * aQuad;
    vec3 clip   = uM * vec3(world, 1.0);
    gl_Position = vec4(clip.xy, 0.0, 1.0);
    vUV         = aQuad;
    vProgress   = aProgress;
    vSeed       = aSeed;
    vOuterColor = aOuterColor;
}`;

const EXPLOSION_FRAG = `#version 300 es
precision mediump float;

in vec2  vUV;
in float vProgress;
in float vSeed;
in vec3  vOuterColor;

out vec4 fragColor;

// Hardcoded warm amber core. Capped well below white so several overlapping
// explosions stack to amber-yellow rather than a featureless white blob.
const vec3 HOT_CORE = vec3(1.0, 0.55, 0.12);

void main() {
    float d    = length(vUV);
    float prog = vProgress;

    // Animated radius — fast initial growth, plateau toward end.
    float r = 0.85 * pow(prog, 0.25);

    // Light angular wobble: two sine harmonics, seeded so each explosion looks
    // different. Amplitude small enough that the silhouette stays clearly round.
    float ang    = atan(vUV.y, vUV.x);
    float t      = prog * 4.0 + vSeed * 6.283;
    float wobble = (sin(ang * 5.0 + t) + sin(ang * 9.0 - t * 0.7)) * 0.025;
    float edge   = r + wobble;

    float shape = smoothstep(edge + 0.04, edge - 0.05, d);
    if (shape <= 0.0) { fragColor = vec4(0.0); return; }

    // Single radial ramp: amber core → faction rim. No white-hot peak, no smoke.
    float radT  = clamp(d / max(edge, 0.001), 0.0, 1.0);
    vec3  rim   = vOuterColor;
    vec3  colour = mix(HOT_CORE, rim, smoothstep(0.0, 1.0, radT));

    float fade = (1.0 - pow(prog, 3.0)) * shape;
    fragColor  = vec4(colour * fade, fade);   // premultiplied (additive blend)
}`;

const EXPLOSION_FLOATS = 8;   // cx cy radius progress seed or og ob
const MAX_EXPLOSIONS   = 256;

// ---------------------------------------------------------------------------
// Helper: hex → [r, g, b] in 0-1 range  (for shader Float32Array packing)
// ---------------------------------------------------------------------------
/** @type {Map<string, [number, number, number]>} Parse cache — called per fighter per frame; inputs are the bounded set of faction/status colors. */
const _hexToRgb01Cache = new Map();

/**
 * @param {string} hex
 * @returns {[number, number, number]}  Cached — treat as read-only.
 */
export function hexToRgb01(hex) {
    const cached = _hexToRgb01Cache.get(hex);
    if (cached) return cached;
    let h = (hex || '#ffffff').replace('#', '');
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    else if (h.length === 4) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    else if (h.length === 8) h = h.slice(0, 6);
    const rgb = /** @type {[number, number, number]} */ ([
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255
    ]);
    _hexToRgb01Cache.set(hex, rgb);
    return rgb;
}

// ---------------------------------------------------------------------------
// GLSL: instanced fighter capsule shader
//   aQuad    — unit quad [-0.5,0.5]² (per-vertex)
//   aPos     — world position (per-instance)
//   aVel     — velocity / 10 (per-instance; shader computes streak length)
//   aColor   — RGB 0-1 (per-instance)
//   aAlpha   — opacity (per-instance)
//   aWeight  — stroke radius in world units: 0.9/1.0/1.1/1.2 (per-instance)
//   uM       — world → clip matrix (mat3, updated each frame)
// ---------------------------------------------------------------------------
// Glow extends 3 capsule-radii beyond the capsule edge (hardcoded as 3.0 in the GLSL strings).

// ---------------------------------------------------------------------------
// Fighter body — velocity streak (abstract line gesture), matching Canvas2D style.
//
// Instance data:
//   aPos    — world centre of the fighter (aVel / 10 encodes direction)
//   aVel    — velocity / 10 (used for direction only; no speed-stretch)
//   aColor  — RGB 0-1 (faction tint)
//   aAlpha  — opacity
//   aWeight — body radius in world units (0.9-1.35 depending on subtype)
//
// Moving fighters:  line from aPos toward aPos-aVel, width = aWeight.
// Slow fighters:    minimum-length dash so they're still visible.
// Fragment:         smooth soft edge perpendicular to motion; premultiplied alpha.
const FIGHTER_VERT = `#version 300 es
precision highp float;

in vec2  aQuad;         // [-0.5, +0.5]² unit quad
in vec2  aPos;          // world position
in vec2  aVel;          // velX/10, velY/10 (already scaled)
in vec3  aColor;
in float aAlpha;
in float aWeight;

uniform mat3 uM;

out float vPerpDist;    // signed perpendicular distance from centreline
out float vHalfW;
out vec3  vColor;
out float vAlpha;

void main() {
    float speed = length(aVel);
    vec2  fwd   = speed > 0.05 ? aVel / speed : vec2(0.0, -1.0);
    vec2  perp  = vec2(-fwd.y, fwd.x);

    // Tail must reach the exhaust cone tip, which EXHAUST_VERT places at
    // aPos - dir*(speed + aWeight).  Match that exactly so line and exhaust connect.
    // Minimum = aWeight*2 so stationary fighters still show as a short dash.
    float lineLen = max(speed + aWeight, aWeight * 2.0);
    vec2  head    = aPos;
    vec2  tail    = aPos - fwd * lineLen;

    // aQuad.y: +0.5 → head vertex, -0.5 → tail vertex.
    float t       = 0.5 - aQuad.y;   // 0 at head, 1 at tail
    float hw      = aWeight * 0.75;
    vec2  world   = mix(head, tail, t) + aQuad.x * hw * 2.0 * perp;

    vec3 clip     = uM * vec3(world, 1.0);
    gl_Position   = vec4(clip.xy, 0.0, 1.0);

    vPerpDist = aQuad.x * hw * 2.0;
    vHalfW    = hw;
    vColor    = aColor;
    vAlpha    = aAlpha;
}`;

const FIGHTER_FRAG = `#version 300 es
precision mediump float;

in float vPerpDist;
in float vHalfW;
in vec3  vColor;
in float vAlpha;

out vec4 fragColor;

void main() {
    float aa  = max(fwidth(vPerpDist), 0.05);
    float cov = 1.0 - smoothstep(vHalfW - aa, vHalfW + aa, abs(vPerpDist));
    if (cov < 0.005) discard;
    float a = vAlpha * cov;
    fragColor = vec4(vColor * a, a);   // premultiplied
}`;

// ---------------------------------------------------------------------------
// Engine exhaust plume — teardrop flame behind each moving fighter.
// Rendered BEFORE the fighter body (lower z-order).
// Core = faction color, outer = white; fades to transparent at the back.
// ---------------------------------------------------------------------------
const EXHAUST_VERT = `#version 300 es
precision highp float;

in vec2  aQuad;
in vec2  aPos;
in vec2  aVel;
in vec3  aColor;
in float aAlpha;
in float aWeight;

uniform mat3 uM;

out vec2  vEx;   // (perpendicular, backward-depth) relative to tail tip
out vec3  vColor;
out float vAlpha;
out float vExLen; // total exhaust length

void main() {
    float speed = length(aVel);
    // No exhaust when stationary — push off-screen.
    if (speed < 0.3) {
        gl_Position = vec4(9999.0, 9999.0, 0.0, 1.0);
        return;
    }

    vec2 dir  = aVel / speed;
    vec2 perp = vec2(-dir.y, dir.x);

    float exLen = (speed * 1.2 + aWeight * 2.0) * 0.5; // half length
    float exW   = aWeight * 2.5 * 0.5;                 // half width

    // Fighter rear = centre - dir*halfHeight = (aPos - dir*speed/2) - dir*(speed/2 + aWeight)
    //             = aPos - dir*(speed + aWeight)
    vec2 tailTip = aPos - dir * (speed + aWeight);
    vec2 centre  = tailTip - dir * exLen * 0.5;

    // Quad covers the exhaust area; aQuad.y mapped along backward direction.
    vec2 world = centre
        + aQuad.x * exW   * perp
        + aQuad.y * exLen * (-dir); // -dir = backward

    vec3 clip = uM * vec3(world, 1.0);
    gl_Position = vec4(clip.xy, 0.0, 1.0);

    vec2 offset = world - tailTip;
    vEx    = vec2(dot(offset, perp), -dot(offset, dir)); // x=perp, y=backward depth
    vColor = aColor;
    vAlpha = aAlpha;
    vExLen = exLen;
}`;

const EXHAUST_FRAG = `#version 300 es
precision mediump float;

in  vec2  vEx;    // x = perpendicular, y = depth behind nozzle (0=nozzle, vExLen=tip)
in  vec3  vColor;
in  float vAlpha;
in  float vExLen;

out vec4 fragColor;

void main() {
    // progress: 0 = nozzle (wide end), 1 = flame tip (point)
    float progress = clamp(vEx.y / vExLen, 0.0, 1.0);

    // Linear cone — full width at nozzle, zero at tip.
    float halfW = max(vExLen * 0.38 * (1.0 - progress), 0.001);

    float dist = abs(vEx.x);

    // Core cone: filled, fades toward tip and edge.
    float r = dist / halfW;
    float coneFade = (1.0 - progress) * max(0.0, 1.0 - r * r);

    // Glow halo: soft exponential falloff beyond the cone edge (additive blend does the rest).
    float glowDist = max(0.0, dist - halfW);
    float glowHalo = exp(-glowDist / max(halfW * 0.4, 0.5)) * (1.0 - progress) * 0.45;

    float totalFade = coneFade + glowHalo;
    if (totalFade < 0.005) discard;

    // Color: white at center axis, faction color at outer edge / glow.
    float colorR = clamp(r + glowDist / halfW, 0.0, 1.0);
    vec3 color = mix(vec3(1.0), vColor, colorR);

    float a = vAlpha * totalFade;
    fragColor = vec4(color * a, a); // premultiplied alpha
}`;

const FIGHTER_FLOATS  = 9;   // per instance: x y vx vy r g b alpha weight
const MAX_FIGHTERS    = 16384;

// ---------------------------------------------------------------------------
// Helper: hex color string → PixiJS 0xRRGGBB number
// Handles: #RGB (3-char), #RGBA (4-char CSS4), #RRGGBB, #RRGGBBAA,
// and rgb(r,g,b) / rgba(r,g,b,a) strings produced by blendHexColors().
// ---------------------------------------------------------------------------
/**
 * @param {string} hex
 * @returns {number}
 */
/** @type {Map<string, number>} Parse cache for hex-form inputs (bounded color tables). rgb()/rgba() blend strings vary continuously, so they stay uncached. */
const _hexToNumCache = new Map();

/**
 * @param {string} hex
 * @returns {number}
 */
export function hexToNum(hex) {
    const s = (hex || '#ffffff').trim();
    // rgb() / rgba() — produced by blendHexColors()
    if (s.startsWith('rgb')) {
        const m = s.match(/(\d+)[^,\d]+(\d+)[^,\d]+(\d+)/);
        if (m) return (parseInt(m[1]) << 16) | (parseInt(m[2]) << 8) | parseInt(m[3]);
        return 0xffffff;
    }
    const cached = _hexToNumCache.get(s);
    if (cached !== undefined) return cached;
    let h = s.replace('#', '');
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    else if (h.length === 4) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    else if (h.length === 8) h = h.slice(0, 6);
    const num = parseInt(h, 16);
    _hexToNumCache.set(s, num);
    return num;
}

// ---------------------------------------------------------------------------
// PixiJS-native particle effects — drawn into _effectsLayer each frame.
// These mirror the Canvas2D Explosion / Fireworks in effects.js but output
// PixiJS Graphics calls so they are included in the bloom render pass.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Instanced fireworks shader — adapted from "Fireworks" by Martijn Steinrucken
// (BigWings / The Art of Code, CC BY-NC-SA 3.0).
// One quad per firework burst; 32 particles simulated entirely in the GPU frag
// shader, no CPU particle array.  Additive blend.
//
// Instance layout (32 bytes / 8 floats):
//   [cx, cy, radius, time, seed, r, g, b]
// ---------------------------------------------------------------------------
const FIREWORKS_VERT = `#version 300 es
precision highp float;

in vec2  aQuad;
in vec2  aCenter;
in float aRadius;
in float aTime;
in float aSeed;
in vec3  aColor;

uniform mat3 uM;

out vec2  vUV;
out float vTime;
out float vSeed;
out vec3  vColor;

void main() {
    vec2 world  = aCenter + aRadius * aQuad;
    vec3 clip   = uM * vec3(world, 1.0);
    gl_Position = vec4(clip.xy, 0.0, 1.0);
    vUV   = aQuad;
    vTime = aTime;
    vSeed = aSeed;
    vColor = aColor;
}`;

const FIREWORKS_FRAG = `#version 300 es
precision mediump float;

in vec2  vUV;
in float vTime;
in float vSeed;
in vec3  vColor;

out vec4 fragColor;

#define NUM_PARTICLES ${FIREWORKS_PARTICLE_COUNT}.
#define MOD3 vec3(.1031,.11369,.13787)

vec3 hash31(float p) {
    vec3 p3 = fract(vec3(p) * MOD3);
    p3 += dot(p3, p3.yzx + 19.19);
    return fract(vec3((p3.x+p3.y)*p3.z, (p3.x+p3.z)*p3.y, (p3.y+p3.z)*p3.x));
}

// Point-light glow: bright inside sz radius, falls off as 1/r²
float glow(vec2 uv, vec2 pos, float sz) {
    uv -= pos;
    sz *= sz;
    return sz / max(dot(uv,uv), 1e-6);
}

void main() {
    float t = vTime;
    if (t <= 0.0 || t >= 1.0) { fragColor = vec4(0.0); return; }

    vec3 col = vec3(0.0);

    for (float i = 0.0; i < NUM_PARTICLES; i++) {
        vec3 n = hash31(i + vSeed * 137.13) - 0.5;

        // Ease: burst outward, slow at end
        float pt = 1.0 - pow(t - 1.0, 2.0);

        // Spread radially with randomised distance
        vec2 endP = normalize(n.xy) * (0.5 + abs(n.z) * 0.5) * 0.9;

        // Trajectory with subtle gravity droop
        vec2 pos = mix(vec2(0.0), endP, pt);
        pos.y -= 0.07 * t * t;

        // Particle size: small hot core fading as it travels
        float sz = 0.011 * (1.0 - pt * 0.85);

        // Brief sparkle shimmer as particles decelerate
        vec3 sn = hash31(i * 13.7 + vSeed * 7.3);
        float sparkle = pow(abs(sin((pt + sn.z) * 75.0)), 7.0) * 0.007 * (1.0 - t);
        sz += sparkle;

        col += vColor * glow(vUV, pos, sz);
    }

    // Fade out over full duration; tone-map so many overlapping particles
    // stay warm rather than going flat white.
    float fade = pow(1.0 - t, 0.5);
    col *= fade;
    col  = col / (col + 0.7);   // Reinhard-style soft cap

    // Output alpha=0 so no contribution to the RT alpha channel.
    // With additive blend (GL_ONE + GL_ONE) the color still adds correctly;
    // outputting alpha=1 would make dark (col≈0) pixels opaque, producing a
    // visible black square after the burst finishes.
    fragColor = vec4(col, 0.0);
}`;

const FIREWORKS_FLOATS = 8;   // cx cy radius time seed r g b
const FIREWORKS_RADIUS = 140; // world-unit radius of the instanced quad
const MAX_FIREWORKS   = 64;

// WebGL effect classes (WebGLExplosion, WebGLFireworks, WebGLIconEffect,
// WebGLPowerImpact) are imported from render/webgl/effects.js above.
// Their factories are registered via buildWebGLEffectFactories(this) in the
// constructor and re-registered by the effectSystem getter whenever
// Game.loadLevel swaps in a fresh EffectSystem.

/**
 * PixiJS-backed live renderer.
 */

export {
    NEBULA_VERT, NEBULA_FRAG,
    PLANET_CONST, _PC,
    _glFract, _glPcg1, _glPcg2,
    _planetSphereFill, _glPlanetIsRocky,
    _rockyPlanetSphereFill,
    PLANET_VERT, _PLANET_FRAG_GAS, PLANET_FRAG_ROCKY,
    EXPLOSION_VERT, EXPLOSION_FRAG,
    EXPLOSION_FLOATS, MAX_EXPLOSIONS,
    FIGHTER_VERT, FIGHTER_FRAG,
    FIGHTER_FLOATS, MAX_FIGHTERS,
    EXHAUST_VERT, EXHAUST_FRAG,
    FIREWORKS_VERT, FIREWORKS_FRAG,
    FIREWORKS_FLOATS, FIREWORKS_RADIUS, MAX_FIREWORKS,
};
